(function () {
  // このファイルは複数のセクション／スニペットから読み込まれる可能性があるため、
  // 二重読み込みでクラス再宣言エラーにならないよう即時関数で包み、定義済みなら何もしない。
  if (customElements.get('customer-fields')) return;

/**
 * `CustomerFields` クラスは、カスタマーフォームのバリデーションとフィールドの制御を行うためのカスタム要素です。
 *
 * 主な機能:
 * - フォームフィールドのバリデーション
 * - パスワードの一致確認
 * - 必須フィールドのチェック
 * - フォームフィールドの表示/非表示の制御
 *
 * メソッド:
 * - `validatePasswordMatch(event)`: パスワードと確認用パスワードが一致するかを確認します。
 * - `showAlert(el, message)`: 指定された要素にエラーメッセージを表示します。
 * - `hideAlert(el, message)`: 指定された要素からエラーメッセージを非表示にします。
 * - `validateRequired(event)`: 必須フィールドのバリデーションを行います。
 * - `validateInt(event)`: 整数フィールドのバリデーションを行います。
 * - `validateFullWidth(event)`: 全角文字フィールドのバリデーションを行います。
 * - `validateHalfWidth(event)`: 半角文字フィールドのバリデーションを行います。
 * - `validateFullWidthKana(event)`: 全角カナフィールドのバリデーションを行います。
 * - `validateEmail(event)`: メールアドレスフィールドのバリデーションを行います。
 * - `validateConformEmail(event)`: 確認用メールアドレスフィールドのバリデーションを行います。
 * - `validatePassword(event)`: パスワードフィールドのバリデーションを行います。
 * - `checkValidity()`: フォーム全体のバリデーションを行います。
 */
class CustomerFields extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    // フォーム内にはダイアログのトリガー等、送信以外の button も置かれるため送信ボタンを明示的に拾う
    this.submit_button = this.querySelector('.btn-account') || this.querySelector('button');
    this.inputs = this.querySelectorAll('input');
    this.full_width_input = this.querySelectorAll('[data-cf-validation="full-width"]');
    this.full_width_kana_input = this.querySelectorAll('[data-cf-validation="full-width-kana"]');
    this.harf_width_input = this.querySelectorAll('[data-cf-validation="half-width"]');
    this.int_input = this.querySelectorAll('[data-cf-validation="int"]');
    this.length_input = this.querySelectorAll('[data-cf-length], [data-cf-maxlength]');
    this.format_input = this.querySelectorAll('[data-cf-format]');
    this.required_input = this.querySelectorAll('[data-cf-required="true"]');
    this.mail_input = this.querySelector('input[name="email"]');
    this.mail_confirm_input = this.querySelector('input[name="email_confirmation"]');
    this.password_input = this.querySelector('input[name="password"]');
    this.password_confirm_input = this.querySelector('input[name="password_confirm"]');

    this.ERROR_MESSAGES = {
      REQUIRED: 'このフィールドは必須です。',
      FULL_WIDTH: '全角文字を入力してください。',
      FULL_WIDTH_KANA: '全角カナを入力してください。',
      HALF_WIDTH: '半角文字を入力してください。',
      EMAIL: '有効なメールアドレスを入力してください。',
      EMAIL_CONFIRM: 'メールアドレスが一致しません。',
      PASSWORD: '半角英数字の組み合わせで「5文字」以上「20文字」以下で入力してください。',
      PASSWORD_CONFIRM: 'パスワードが一致しません。',
      INT: '半角数字を入力してください。',
      // {n} は桁数に置換して使う
      LENGTH_EXACT: '{n}桁の半角数字で入力してください。',
      LENGTH_MAX: '{n}桁以内の半角数字で入力してください。',
    };

    // 編集フォームでは初期値が入っているため、入力済みの値を初期値として保持する
    this.emailValue = this.mail_input?.value || '';
    this.passwordValue = '';

    this.setupValidators();
  }

  setupValidators() {
    this.full_width_input?.forEach((input) => {
      input.addEventListener('change', this.validateFullWidth.bind(this));
    });

    this.full_width_kana_input?.forEach((input) => {
      input.addEventListener('change', this.validateFullWidthKana.bind(this));
    });

    this.harf_width_input?.forEach((input) => {
      input.addEventListener('change', this.validateHalfWidth.bind(this));
    });

    this.int_input?.forEach((input) => {
      input.addEventListener('change', this.validateInt.bind(this));
    });

    this.length_input?.forEach((input) => {
      input.addEventListener('change', this.validateLength.bind(this));
    });

    this.required_input?.forEach((input) => {
      input.addEventListener('change', this.validateRequired.bind(this));
    });

    // 入力値の整形は検証より先に済ませたいので change ではなく input で受ける。
    // IME 変換中は formatValue 側で整形を見送るため、確定時（compositionend）にも整形する。
    // 既存データに区切り文字が含まれている場合があるため、初期表示時にも一度整形しておく。
    this.format_input?.forEach((input) => {
      input.addEventListener('input', this.formatValue.bind(this));
      input.addEventListener('compositionend', this.formatValue.bind(this));
      this.formatValue({ target: input });
    });

    this.mail_input?.addEventListener('change', this.validateEmail.bind(this));
    this.mail_confirm_input?.addEventListener('change', this.validateConformEmail.bind(this));
    this.password_input?.addEventListener('change', this.validatePassword.bind(this));
    this.password_confirm_input?.addEventListener('change', this.validatePasswordMatch.bind(this));

    this.submit_button?.addEventListener('click', this.checkValidity.bind(this));
  }

  /**
   * 表示中の全項目に対して、必須・文字種・桁数のバリデーションをまとめて実行する。
   * change イベントを一度も発火していない項目（未入力のまま送信・オートフィル等）を
   * 取りこぼさないよう、送信直前に必ず呼ぶ。
   */
  runAllValidations() {
    this.required_input.forEach((input) => this.validateRequired({ target: input }));
    this.full_width_input.forEach((input) => this.validateFullWidth({ target: input }));
    this.full_width_kana_input.forEach((input) => this.validateFullWidthKana({ target: input }));
    this.harf_width_input.forEach((input) => this.validateHalfWidth({ target: input }));
    this.int_input.forEach((input) => this.validateInt({ target: input }));
    this.length_input.forEach((input) => this.validateLength({ target: input }));
  }

  checkValidity() {
    this.runAllValidations();
    const hasError = this.querySelector('.cf-error[style*="display: block"]');
    if (this.form.checkValidity() && !hasError) {
      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
      this.form.dispatchEvent(submitEvent);
    } else {
      this.scrollToFirstError();
    }
  }

  /**
   * 祖先要素からスクロール可能なコンテナ（モーダル等）を探す。
   * 見つからない場合は window スクロールとみなして null を返す。
   */
  getScrollContainer(el) {
    let parent = el.parentElement;

    while (parent && parent !== document.body) {
      const overflowY = window.getComputedStyle(parent).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  /**
   * 表示中のエラーのうち、最初のものまでスクロールしてフォーカスする。
   */
  scrollToFirstError() {
    const OFFSET = 20;
    const errorElement = Array.from(this.querySelectorAll('.cf-error[style*="display: block"]')).find(
      (el) => !el.closest('dd')?.hasAttribute('disabled')
    );

    if (!errorElement) {
      return;
    }

    // 項目名（dt）から見えるようにスクロールする
    const description = errorElement.closest('dd');
    const term = description?.previousElementSibling;
    const target = term?.tagName === 'DT' ? term : description || errorElement;
    const scrollContainer = this.getScrollContainer(target);

    if (scrollContainer) {
      const top =
        target.getBoundingClientRect().top -
        scrollContainer.getBoundingClientRect().top +
        scrollContainer.scrollTop -
        OFFSET;
      scrollContainer.scrollTo({ top, behavior: 'smooth' });
    } else {
      const headerHeight =
        parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - OFFSET;
      window.scrollTo({ top, behavior: 'smooth' });
    }

    const name = errorElement.getAttribute('data-cf-field');
    const input = name ? this.querySelector(`[name="${name}"]:not([disabled])`) : null;
    input?.focus({ preventScroll: true });
  }

  showAlert(el, message) {
    const name = el.getAttribute('name');
    const errorElement = this.querySelector(`.cf-error[data-cf-field="${name}"]`);

    if (!errorElement) {
      console.warn(`Error element not found for input:`, el);
      return;
    }

    let shouldAddMessage = true;
    const errorMessages = errorElement.querySelectorAll('.cf-error-message');
    
    // Check if the error message already exists
    if (errorMessages && errorMessages.length > 0) {
      const messagesList = Array.from(errorMessages);
      shouldAddMessage = !messagesList.some(item => item.textContent === message);
    }

    if (shouldAddMessage) {
      errorElement.innerHTML += `<div class="cf-error-message">${message}</div>`;
      errorElement.style.display = 'block';
    }

    this.markInvalid(el, true);
  }

  /**
   * エラーの有無を項目のコンテナに反映する（枠線を警告色にするため）。
   * コンテナが見つからない場合は入力欄自身に付ける。
   */
  markInvalid(el, invalid) {
    // 生年月日のように入力欄が hidden の項目は、hidden 自体に見た目が無いため
    // 項目のまとまり（dd）へ付けて、その中の入力欄をまとめて警告色にする。
    const target =
      el.type === 'hidden' ? el.closest('dd') || el : el.closest('.field, .address-form__field') || el;

    target.classList.toggle('cf-invalid', invalid);
  }

  hideAlert(el, message) {
    const name = el.getAttribute('name');
    const errorElement = this.querySelector(`.cf-error[data-cf-field="${name}"]`);

    if (!errorElement) {
      return;
    }

    const errorMessages = errorElement.querySelectorAll('.cf-error-message');
    const errorMessagesApp = errorElement.querySelectorAll('p');

    errorMessages.forEach((errorMessage) => {
      if (errorMessage.textContent === message) {
        errorMessage.remove();
      }
    });
    errorMessagesApp.forEach((errorMessage) => {
      errorMessage.remove();
    });
    if (errorElement.children.length === 0) {
      errorElement.style.display = 'none';
      this.markInvalid(el, false);
    }
  }

  validateRequired(event) {
    const input = event.target;
    const value = input.type === 'checkbox' ? input.checked : input.value;

    if (input.disabled) {
      return;
    }

    if (value === '' || value === false) {
      this.showAlert(input, this.ERROR_MESSAGES.REQUIRED);
    } else {
      this.hideAlert(input, this.ERROR_MESSAGES.REQUIRED);
    }
  }

  validateInt(event) {
    const input = event.target;
    const name = input.value;

    if (name === '' || /^[0-9]*$/.test(name)) {
      this.hideAlert(input, this.ERROR_MESSAGES.INT);
    } else {
      this.showAlert(input, this.ERROR_MESSAGES.INT);
    }
  }

  /**
   * 桁数チェック。
   * - `data-cf-length="7"`    … ちょうど7桁（郵便番号など固定長）
   * - `data-cf-maxlength="15"` … 15桁以内（電話番号など上限）
   * 未入力は「必須チェック」の担当なのでここでは通す。
   */
  validateLength(event) {
    const input = event.target;
    const value = input.value || '';
    const exact = input.getAttribute('data-cf-length');
    const max = input.getAttribute('data-cf-maxlength');

    if (exact) {
      const message = this.ERROR_MESSAGES.LENGTH_EXACT.replace('{n}', exact);
      if (value === '' || value.length === Number(exact)) {
        this.hideAlert(input, message);
      } else {
        this.showAlert(input, message);
      }
    }

    if (max) {
      const message = this.ERROR_MESSAGES.LENGTH_MAX.replace('{n}', max);
      if (value === '' || value.length <= Number(max)) {
        this.hideAlert(input, message);
      } else {
        this.showAlert(input, message);
      }
    }
  }

  /**
   * 入力値をその場で整形する。
   * - `data-cf-format="digits"` … 数字以外（ハイフン・スペース等）を取り除いて数字だけにする
   *
   * 全角数字は「除去」ではなく半角へ「変換」してから取り出す。
   * 単純に数字以外を消すと、IME で入力された「１２３４５６７」が丸ごと消えてしまうため。
   *
   * 桁数の上限は `data-cf-length`（固定長）／`data-cf-maxlength`（上限）を流用する。
   * 郵便番号は `maxlength` 属性ではなくここで抑止している。
   * `maxlength="7"` のままだと「123-4567」を貼り付けた時点でブラウザが7文字目で切ってしまい、
   * ハイフンを除いても6桁にしかならず、住所の自動入力も発火しなくなるため。
   */
  formatValue(event) {
    const input = event.target;

    if (input.getAttribute('data-cf-format') !== 'digits') {
      return;
    }

    // IME 変換中（未確定）に value を書き換えると、IME が保持している未確定文字列と食い違い、
    // Windows の Chrome／Edge では「123456」が「112233…」のように重複して入力される（MIKIMOTO_PHARMA_DEV-507）。
    // 変換中は触らず、確定時の compositionend で整形する。
    if (event.isComposing) {
      return;
    }

    const before = input.value || '';
    const limit = Number(input.getAttribute('data-cf-length') || input.getAttribute('data-cf-maxlength')) || 0;
    let after = this.toHalfWidthDigits(before).replace(/[^0-9]/g, '');

    if (limit > 0) {
      after = after.slice(0, limit);
    }

    if (after === before) {
      return;
    }

    // 途中の文字を消したときにカーソルが末尾へ飛ばないよう、
    // 「カーソルより前にあった数字の個数」を整形後のカーソル位置として復元する。
    const caret = input.selectionStart;
    const digitsBeforeCaret =
      caret === null ? null : this.toHalfWidthDigits(before.slice(0, caret)).replace(/[^0-9]/g, '').length;

    input.value = after;

    if (digitsBeforeCaret !== null && input.type === 'text' && document.activeElement === input) {
      const position = Math.min(digitsBeforeCaret, after.length);
      input.setSelectionRange(position, position);
    }
  }

  // 全角数字（０-９）を半角へ変換する
  toHalfWidthDigits(str) {
    if (!str) return str;
    return str.replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0));
  }

  validateHalfWidth(event) {
    const input = event.target;
    const name = input.value;

    if (name === '' || /^[\x01-\x7E\uFF61-\uFF9F]*$/.test(name)) {
      this.hideAlert(input, this.ERROR_MESSAGES.HALF_WIDTH);
    } else {
      this.showAlert(input, this.ERROR_MESSAGES.HALF_WIDTH);
    }
  }

  // \u534A\u89D2\uFF08\u82F1\u6570\u30FB\u8A18\u53F7\u30FB\u30B9\u30DA\u30FC\u30B9\u30FB\u30AB\u30CA\uFF09\u3092\u5168\u89D2\u306B\u5909\u63DB\u3059\u308B
  toFullWidth(str) {
    if (!str) return str;
    const kanaMap = {
      \uFF76\uFF9E: '\u30AC', \uFF77\uFF9E: '\u30AE', \uFF78\uFF9E: '\u30B0', \uFF79\uFF9E: '\u30B2', \uFF7A\uFF9E: '\u30B4',
      \uFF7B\uFF9E: '\u30B6', \uFF7C\uFF9E: '\u30B8', \uFF7D\uFF9E: '\u30BA', \uFF7E\uFF9E: '\u30BC', \uFF7F\uFF9E: '\u30BE',
      \uFF80\uFF9E: '\u30C0', \uFF81\uFF9E: '\u30C2', \uFF82\uFF9E: '\u30C5', \uFF83\uFF9E: '\u30C7', \uFF84\uFF9E: '\u30C9',
      \uFF8A\uFF9E: '\u30D0', \uFF8B\uFF9E: '\u30D3', \uFF8C\uFF9E: '\u30D6', \uFF8D\uFF9E: '\u30D9', \uFF8E\uFF9E: '\u30DC',
      \uFF8A\uFF9F: '\u30D1', \uFF8B\uFF9F: '\u30D4', \uFF8C\uFF9F: '\u30D7', \uFF8D\uFF9F: '\u30DA', \uFF8E\uFF9F: '\u30DD',
      \uFF73\uFF9E: '\u30F4',
      \uFF71: '\u30A2', \uFF72: '\u30A4', \uFF73: '\u30A6', \uFF74: '\u30A8', \uFF75: '\u30AA',
      \uFF76: '\u30AB', \uFF77: '\u30AD', \uFF78: '\u30AF', \uFF79: '\u30B1', \uFF7A: '\u30B3',
      \uFF7B: '\u30B5', \uFF7C: '\u30B7', \uFF7D: '\u30B9', \uFF7E: '\u30BB', \uFF7F: '\u30BD',
      \uFF80: '\u30BF', \uFF81: '\u30C1', \uFF82: '\u30C4', \uFF83: '\u30C6', \uFF84: '\u30C8',
      \uFF85: '\u30CA', \uFF86: '\u30CB', \uFF87: '\u30CC', \uFF88: '\u30CD', \uFF89: '\u30CE',
      \uFF8A: '\u30CF', \uFF8B: '\u30D2', \uFF8C: '\u30D5', \uFF8D: '\u30D8', \uFF8E: '\u30DB',
      \uFF8F: '\u30DE', \uFF90: '\u30DF', \uFF91: '\u30E0', \uFF92: '\u30E1', \uFF93: '\u30E2',
      \uFF94: '\u30E4', \uFF95: '\u30E6', \uFF96: '\u30E8',
      \uFF97: '\u30E9', \uFF98: '\u30EA', \uFF99: '\u30EB', \uFF9A: '\u30EC', \uFF9B: '\u30ED',
      \uFF9C: '\u30EF', \uFF66: '\u30F2', \uFF9D: '\u30F3',
      \uFF67: '\u30A1', \uFF68: '\u30A3', \uFF69: '\u30A5', \uFF6A: '\u30A7', \uFF6B: '\u30A9',
      \uFF6F: '\u30C3', \uFF6C: '\u30E3', \uFF6D: '\u30E5', \uFF6E: '\u30E7',
      '\uFF61': '\u3002', '\uFF62': '\u300C', '\uFF63': '\u300D', '\uFF64': '\u3001', '\uFF65': '\u30FB', \uFF70: '\u30FC', \uFF9E: '\u309B', \uFF9F: '\u309C',
    };
    const reg = new RegExp('(' + Object.keys(kanaMap).join('|') + ')', 'g');
    let result = str.replace(reg, (m) => kanaMap[m]);
    // \u534A\u89D2\u82F1\u6570\u30FB\u8A18\u53F7 \u2192 \u5168\u89D2
    result = result.replace(/[!-~]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) + 0xfee0));
    // \u534A\u89D2\u30B9\u30DA\u30FC\u30B9 \u2192 \u5168\u89D2\u30B9\u30DA\u30FC\u30B9
    result = result.replace(/ /g, '\u3000');
    return result;
  }

  validateFullWidth(event) {
    const input = event.target;
    const converted = this.toFullWidth(input.value);
    if (converted !== input.value) {
      input.value = converted;
    }
    const name = input.value;
    const fullWidthPattern =
      /^[\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\uFF01-\uFF60\uFFA0-\uFFEF\u4E00-\u9FAF\u3400-\u4DBF\uF900-\uFAFF]+$/;

    if (name === '' || fullWidthPattern.test(name)) {
      this.hideAlert(input, this.ERROR_MESSAGES.FULL_WIDTH);
    } else {
      this.showAlert(input, this.ERROR_MESSAGES.FULL_WIDTH);
    }
  }

  validateFullWidthKana(event) {
    const input = event.target;
    const converted = this.toFullWidth(input.value);
    if (converted !== input.value) {
      input.value = converted;
    }
    const name = input.value;

    if (name === '' || /^[ァ-ンヴー]*$/.test(name)) {
      this.hideAlert(input, this.ERROR_MESSAGES.FULL_WIDTH_KANA);
    } else {
      this.showAlert(input, this.ERROR_MESSAGES.FULL_WIDTH_KANA);
    }
  }

  validateEmail(event) {
    const input = event.target;
    const email = input.value;
    this.emailValue = email;

    if (email === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+(\.[^\s@]+)*$/.test(email)) {
      this.hideAlert(input, this.ERROR_MESSAGES.EMAIL);
    } else {
      this.showAlert(input, this.ERROR_MESSAGES.EMAIL);
    }
    if (this.mail_confirm_input) {
      this.validateConformEmail({ target: this.mail_confirm_input });
    }
  }

  validateConformEmail(event) {
    const input = event.target;
    if (!input) return;
    const confirm_mail = input.value;
    const email = this.emailValue;

    if (email !== confirm_mail) {
      this.showAlert(input, this.ERROR_MESSAGES.EMAIL_CONFIRM);
    } else {
      this.hideAlert(input, this.ERROR_MESSAGES.EMAIL_CONFIRM);
    }
  }

  validatePassword(event) {
    const input = event.target;
    const password = input.value;
    this.passwordValue = password;

    if (password !== '' && /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z0-9]{5,20}$/.test(password)) {
      this.hideAlert(input, this.ERROR_MESSAGES.PASSWORD);
    } else {
      this.showAlert(input, this.ERROR_MESSAGES.PASSWORD);
    }
    //this.validatePasswordMatch({ target: this.querySelector('input[name="password_confirm"]') });
  }

  validatePasswordMatch(event) {
    const input = event.target;
    const confirm_password = input.value;
    const password = this.passwordValue;

    if (password !== confirm_password) {
      this.showAlert(input, this.ERROR_MESSAGES.PASSWORD_CONFIRM);
    } else {
      this.hideAlert(input, this.ERROR_MESSAGES.PASSWORD_CONFIRM);
    }
  }
}

/**
 * `CustomerFieldsSwitcher` クラスは、`CustomerFields` クラスを拡張し、
 * フォームの値に基づいてフィールドの表示/非表示を切り替える機能を追加します。
 *
 * 主な機能:
 * - フォームの値に基づいてフィールドの表示/非表示を切り替える
 * - 初期値に基づいてフィールドの状態を設定する
 *
 * メソッド:
 * - `controlFormFieldsByValue(event)`: フォームの値に基づいてフィールドの表示/非表示を制御します。
 */
class CustomerFieldsSwitcher extends CustomerFields {
  constructor() {
    super();
    this.member_ship_type = this.querySelectorAll('input[name="mf.ba_customer_fields.type"]');
    this.checked_member_ship_types = Array.from(this.member_ship_type).filter((input) => input.checked);
    this.industry_select = this.querySelector('select[name="mf.ba_customer_fields.industry"]');

    this.member_ship_type.forEach((input) => {
      input.addEventListener('change', this.controlFormFieldsByValue.bind(this));
    });

    // 初期値を設定
    const initialInput = this.checked_member_ship_types[0] || this.member_ship_type[0]; // 初期値として最初の要素を選択
    if (initialInput) {
      initialInput.checked = true; // 必要に応じてチェックを設定
      const event = new Event('change');
      initialInput.dispatchEvent(event); // change イベントをディスパッチ
    }
  }

  validateRequired(event) {
    const input = event.target;
    const value = input.type === 'checkbox' ? input.checked : input.value;

    const parentElement = input.closest('dd'); // 親要素を取得
    const parentStyle = window.getComputedStyle(parentElement);

    if (input.disabled || parentStyle.display === 'none') {
      return;
    }

    if (value === '' || value === false) {
      this.showAlert(input, this.ERROR_MESSAGES.REQUIRED);
    } else {
      this.hideAlert(input, this.ERROR_MESSAGES.REQUIRED);
    }
  }

  checkValidity() {
    this.runAllValidations();
    const hasError = this.querySelector('dd:not([disabled]) .cf-error[style*="display: block"]');
    if (!hasError) {
      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
      this.form.dispatchEvent(submitEvent);
    } else {
      this.scrollToFirstError();
    }
  }

  controlFormFieldsByValue(event) {
    const input = event.target;
    const value = input.value;
    const elementsToControl = this.querySelectorAll('[data-cf-member-ship-type]');

    elementsToControl.forEach((element) => {
      const types = element.getAttribute('data-cf-member-ship-type').split(',');
      const industry_value = this.industry_select.value;

      if (element.tagName === 'OPTION') {
        if (types.includes(value)) {
          element.style.display = 'block';
        } else {
          if (industry_value === element.value) {
            this.industry_select.selectedIndex = '';
          }
          element.style.display = 'none';
        }
      } else if (types.includes(value)) {
        element.style.display = 'block';
        element.removeAttribute('disabled');
        element.querySelectorAll('input, select').forEach((input) => {
          input.removeAttribute('disabled');
        });
      } else {
        element.style.display = 'none';
        element.setAttribute('disabled', 'disabled');
        element.querySelectorAll('input, select').forEach((input) => {
          input.setAttribute('disabled', 'disabled');
        });
      }
    });
  }
}

class ResetPasswordFields extends CustomerFields {
  constructor() {
    super();
    this.password_input = this.querySelector('input[name="customer[password]"]');
    this.password_confirm_input = this.querySelector('input[name="customer[password_confirmation]"]');
    
    if (this.password_input) {
      this.password_input.addEventListener('change', this.validatePassword.bind(this));
    }
    
    if (this.password_confirm_input) {
      this.password_confirm_input.addEventListener('change', this.validatePasswordMatch.bind(this));
    }
  }
  
  // Execute when form submit button is clicked
  checkValidity(event) {
    if (event) {
      event.preventDefault();
    }
    if (this.password_input) {
      this.validatePassword({ target: this.password_input });
    }
    
    if (this.password_confirm_input) {
      this.validatePasswordMatch({ target: this.password_confirm_input });
    }
    
    const hasError = this.querySelector('.cf-error[style*="display: block"]');
    
    if (!hasError && this.form && this.form.checkValidity()) {
      this.form.submit();
    } else {
      this.scrollToFirstError();
    }
  }
}

/**
 * `AddressFields` クラスは、お届け先（Shopify 標準 `customer_address` フォーム）に
 * バリデーションを適用するためのカスタム要素です。
 *
 * `CustomerFields` との違い:
 * - 送信は通常の POST。submit イベントの dispatch では送信されないため `form.submit()` を呼ぶ。
 * - 1ページに複数フォーム（住所ごとの編集 + 新規追加）が並ぶため、
 *   エラー表示の探索は `CustomerFields` 側で自要素内に限定済み。
 */
class AddressFields extends CustomerFields {
  /**
   * 画面に表示されていない項目は検証しない。
   * 都道府県は国の選択によってコンテナごと隠れるため、隠れたまま必須エラーになると
   * 「エラーは出ていないのに送信できない」状態になってしまう。
   */
  isRendered(input) {
    return input.offsetParent !== null;
  }

  validateRequired(event) {
    if (!this.isRendered(event.target)) {
      return;
    }
    super.validateRequired(event);
  }

  checkValidity(event) {
    if (event) {
      event.preventDefault();
    }

    this.runAllValidations();

    const hasError = this.querySelector('.cf-error[style*="display: block"]');

    if (!hasError && this.form?.checkValidity()) {
      this.form.submit();
    } else {
      this.scrollToFirstError();
    }
  }

  /**
   * お届け先フォームは dt/dd ではなく `.address-form__field` 単位で項目が並ぶため、
   * スクロール先の求め方を上書きする。
   */
  scrollToFirstError() {
    const OFFSET = 20;
    const errorElement = this.querySelector('.cf-error[style*="display: block"]');

    if (!errorElement) {
      return;
    }

    const target = errorElement.closest('.address-form__field') || errorElement;
    const headerHeight =
      parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - headerHeight - OFFSET, behavior: 'smooth' });

    const name = errorElement.getAttribute('data-cf-field');
    const input = name ? this.querySelector(`[name="${name}"]:not([disabled])`) : null;
    input?.focus({ preventScroll: true });
  }
}

customElements.define('customer-fields', CustomerFieldsSwitcher);
customElements.define('reset-password-fields', ResetPasswordFields);
customElements.define('address-fields', AddressFields);

})();
